import { useRef, useState } from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { Link } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { ChevronRightIcon, MenuIcon, XIcon } from 'lucide-react'

import { authViewTransition, cn } from '@/lib/utils'
import { Button, ButtonLink } from '@/components/ui/button'
import { useSnapDismiss } from '@/components/ui/drawer-snap-dismiss'
import { useTranslation } from '@/lib/locales'
import { LogoIcon } from '@/routes/_app/-components/shared/logo'
import { LocaleFlag } from './footer'
import { DemoChevron } from './demo-chevron'

// The nav's mobile face: below `md` the section links, the language and Sign in have no room in the bar, so a
// burger opens them as a FULL-SCREEN sheet. Its dismiss drag is a native scroll (drawer-snap-dismiss.ts, on the
// horizontal axis), so the compositor owns the gesture; base-ui's Dialog owns focus, Escape and the scroll lock.

export interface MenuSection {
  id: string
  label: string
}

/** The bar's own height and gutters, repeated by the sheet's header so the close lands where the burger was. */
const BAR_ROW = 'flex h-17 shrink-0 items-center px-5 sm:px-6'

export function LandingMobileMenu(props: {
  sections: readonly MenuSection[]
  active?: string
  onContact?: () => void
}) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  /** Run once the sheet has finished closing: the modal locks page scroll, so a jump made while open goes nowhere. */
  const afterClose = useRef<(() => void) | null>(null)
  const dismiss = () => setOpen(false)

  const closeThen = (action: () => void) => {
    afterClose.current = action
    setOpen(false)
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={setOpen}
      onOpenChangeComplete={(nowOpen) => {
        if (nowOpen) return
        const action = afterClose.current
        afterClose.current = null
        action?.()
      }}
    >
      <DialogPrimitive.Trigger
        render={<Button variant="ghost" size="icon-lg" />}
        // `size-11` over the button's 36 px: the burger is a thumb target, and the bar has the height to spare.
        className="-mr-1.5 size-11 md:hidden"
        aria-label={t`Open menu`}
      >
        <MenuIcon className="size-5" />
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        {/* A child of the Portal so it MOUNTS WITH THE SHEET: its refs are live before the first paint, which is
            what lets the scroller be parked on the panel rather than on the dismissed slot. */}
        <MenuSurface {...props} closeThen={closeThen} onDismiss={dismiss} />
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function MenuSurface(props: {
  sections: readonly MenuSection[]
  active?: string
  onContact?: () => void
  closeThen: (action: () => void) => void
  onDismiss: () => void
}) {
  const { t } = useLingui()
  const { language, changeLanguage, languages } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const backdropRef = useRef<HTMLDivElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)
  useSnapDismiss({ scrollerRef, backdropRef, axis: 'x', onDismiss: props.onDismiss })

  return (
    <>
      <DialogPrimitive.Backdrop
        ref={backdropRef}
        data-slot="menu-backdrop"
        className={cn(
          // Over the cookie banner's `z-[1000]`, which otherwise buries the language and Sign in row: the sheet
          // is modal, so the banner is already inert behind it and painting on top only makes it look reachable.
          'bg-background/60 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 z-[1001]',
          // While the finger owns the panel the fade must TRACK it, not chase it through a transition.
          'opacity-[calc(1-var(--sheet-dismiss,0))] transition-opacity duration-200 data-scrolling:duration-0',
          'data-ending-style:opacity-0 data-starting-style:opacity-0',
        )}
      />
      <DialogPrimitive.Popup
        ref={scrollerRef}
        // base-ui hides the background with `aria-hidden` but sets no `aria-modal`; the APG asks for both.
        aria-modal
        data-slot="menu-content"
        initialFocus={closeRef}
        className={cn(
          // THE SCROLLER: two full-width snap slots side by side, parked on the second. `overscroll-x-none` is
          // load-bearing — without it the drag chains out and iOS takes it as the back-swipe navigation gesture.
          'fixed inset-0 z-[1001] flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-none outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          'transition-transform duration-300 ease-out data-ending-style:translate-x-full data-starting-style:translate-x-full motion-reduce:transition-none',
        )}
      >
        {/* What the panel scrolls away INTO: no content and no tap target, since the panel covers the viewport. */}
        <div aria-hidden data-slot="menu-dismiss-area" className="h-full w-full shrink-0 snap-start" />
        {/* The surface is painted HERE, never on the scroller: a background there would fill the empty slot too,
            so a drag would reveal an opaque nothing instead of the page behind. */}
        <div
          data-slot="menu-panel"
          className="bg-background relative flex h-full w-full shrink-0 snap-end flex-col overflow-y-auto overscroll-y-contain"
        >
          <div className={BAR_ROW}>
            {/* A real link, never a `Dialog.Close`: Close is a button, and rendering one over an anchor trades
                the link's own semantics for `role="button"`. Closing is what its `onClick` is for. */}
            <Link
              to="/"
              title="D'Anchiano"
              className="-ml-3 shrink-0 p-3"
              // Already home on the landing (`onContact` is what says so), where the logo's job is the way back up.
              onClick={(event) => {
                if (!props.onContact) return props.onDismiss()
                event.preventDefault()
                props.closeThen(scrollToTop)
              }}
            >
              <LogoIcon />
            </Link>
            <DialogPrimitive.Close
              ref={closeRef}
              render={<Button variant="ghost" size="icon-lg" />}
              className="-mr-1.5 ml-auto size-11"
              aria-label={t`Close menu`}
            >
              <XIcon className="size-5" />
            </DialogPrimitive.Close>
            <DialogPrimitive.Title className="sr-only">{t`Menu`}</DialogPrimitive.Title>
          </div>

          <nav className="flex flex-col px-5 pt-6 sm:px-6">
            {props.sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                aria-current={props.active === section.id ? 'location' : undefined}
                onClick={(event) => {
                  event.preventDefault()
                  props.closeThen(() => scrollToSection(section.id))
                }}
                className={cn(
                  'flex min-h-14 items-center justify-between gap-4 text-2xl transition-colors',
                  props.active === section.id ? 'text-foreground font-medium' : 'text-muted-foreground',
                )}
              >
                {section.label}
                <ChevronRightIcon aria-hidden className="text-muted-foreground/60 size-5 shrink-0" />
              </a>
            ))}
          </nav>

          {/* Sign in leads: it is the returning visitor's errand, and the demo CTA is the one they scroll past it to. */}
          <div className="flex flex-col gap-3 px-5 pt-8 sm:px-6">
            <ButtonLink
              variant="outline"
              size="lg"
              to="/login"
              viewTransition={authViewTransition('enter')}
              className="h-12 w-full"
              onClick={props.onDismiss}
            >
              {t`Sign in`}
            </ButtonLink>
            {props.onContact ? (
              <Button
                size="lg"
                type="button"
                className="h-12 w-full"
                onClick={() => props.onContact && props.closeThen(props.onContact)}
              >
                {t`Get in touch`}
                <DemoChevron />
              </Button>
            ) : (
              <ButtonLink size="lg" to="/" hash="contact" className="h-12 w-full" onClick={props.onDismiss}>
                {t`Get in touch`}
                <DemoChevron />
              </ButtonLink>
            )}
          </div>

          {/* Pinned to the bottom, clear of the home indicator. The language in use is the only one boxed. */}
          <div className="mt-auto flex items-center gap-1 px-5 pt-8 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] sm:px-6">
            {Object.entries(languages).map(([code, label]) => (
              <Button
                key={code}
                type="button"
                variant={language === code ? 'outline' : 'ghost'}
                size="lg"
                onClick={() => changeLanguage(code)}
                aria-current={language === code ? 'true' : undefined}
                aria-label={label}
                className="h-11 gap-1.5 px-2.5"
              >
                <LocaleFlag code={code} />
                <span aria-hidden>{code.slice(0, 2).toUpperCase()}</span>
              </Button>
            ))}
          </div>
        </div>
      </DialogPrimitive.Popup>
    </>
  )
}

const scrollBehavior = (): ScrollBehavior =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'

/** The section tops carry their own `scroll-mt-*`, so landing one under the sticky nav is the browser's job. */
function scrollToSection(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: scrollBehavior() })
}

function scrollToTop(): void {
  window.scrollTo({ top: 0, behavior: scrollBehavior() })
}
